import { createContext, useState, useEffect, useCallback } from 'react'

import { invalidateOnboardingQueryEventDispatch } from '@space-events/invalidateOnboardingQuery'
import { resetFormGeneralConfigEventDispatch } from '@space-events/resetFormGeneralConfig'
import { toggleBindExclusiveCommunitySurveyEventDispatch } from '@space-events/toggleBindExclusiveCommunitySurvey'
import type { ToggleCommunityConfigurationSidebarEvent } from '@space-events/toggleCommunityConfigurationSidebar'

import {
  toggleCommunityConfigurationSidebarEventDispatch,
  toggleCommunityConfigurationSidebarEventListener
} from '@space-events/toggleCommunityConfigurationSidebar'

import { ConfigSteps } from '@space-events/toggleUnbanMemberCommunityModal'
import { useUserPermissions } from '@space-shell/common/hooks/useUserPermissions'
import { PermissionActions, type CommunityProps } from '@space-shell/common/services/communities'

import useMembersImport from '@space-shell/modules/communities/context/membersImportProvider/hook'

import type {
  CommunityConfigurationSidebarContextProps,
  CommunityConfigurationSidebarProviderProps
} from './types'

import CommunityConfigurationSidebar from '../..'

export type CommunityConfigurationSidebarEventsPayload = {
  community?: CommunityProps
} & ToggleCommunityConfigurationSidebarEvent

export const CommunityConfigurationSidebarContext =
  createContext<CommunityConfigurationSidebarContextProps>(
    {} as CommunityConfigurationSidebarContextProps
  )

export const CommunityConfigurationSidebarProvider = ({
  children
}: CommunityConfigurationSidebarProviderProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isCalledFromOnboarding, setIsCalledFromOnboarding] = useState<boolean>(false)
  const [community, setCommunity] = useState<CommunityProps>({} as CommunityProps)
  const { hasPermission } = useUserPermissions()
  const { setIsMembersImportOpen } = useMembersImport()
  const getSidebarStep = () => {
    if (community.isOwner) {
      return ConfigSteps.GENERAL_CONFIG
    }

    if (hasPermission(PermissionActions.ACCESS_AND_PRIVACY, community?.permissions?.actions)) {
      return ConfigSteps.ACCESS_AND_PRIVACY
    }

    return ConfigSteps.MEMBERS_AND_PERMISSIONS
  }

  const [selectedStep, setSelectedStep] = useState<ConfigSteps>(getSidebarStep())

  const updateCommunity = useCallback((updatedCommunity: Partial<CommunityProps>) => {
    setCommunity((prevCommunity) => ({
      ...prevCommunity,
      ...updatedCommunity
    }))
  }, [])

  const reset = useCallback(() => {
    toggleBindExclusiveCommunitySurveyEventDispatch({
      action: 'open'
    })

    setIsOpen(false)

    setSelectedStep(community.isOwner ? ConfigSteps.GENERAL_CONFIG : ConfigSteps.ACCESS_AND_PRIVACY)

    setIsMembersImportOpen(false)
  }, [community.isOwner, setIsMembersImportOpen])

  useEffect(() => {
    toggleCommunityConfigurationSidebarEventListener((event) => {
      const payload = (event as CustomEvent<CommunityConfigurationSidebarEventsPayload>).detail

      switch (payload.action) {
        case 'close':
          window.document.body.classList.remove('_overflow-hidden')

          reset()

          break

        case 'invalidateQueryOnboarding':
          invalidateOnboardingQueryEventDispatch()
          window.document.body.classList.remove('_overflow-hidden')

          break

        case 'open':
          payload?.community && setCommunity(payload.community)

          setIsOpen(true)

          payload?.step !== undefined && setSelectedStep(payload?.step as unknown as ConfigSteps)
          setIsCalledFromOnboarding(Boolean(payload?.isCalledFromOnboarding))

          window.document.body.classList.add('_overflow-hidden')
          break
      }
    })
  }, [reset])

  const openCommunityConfigurationSidebar = useCallback((community: CommunityProps) => {
    toggleCommunityConfigurationSidebarEventDispatch({ action: 'open', community })
  }, [])

  const closeCommunityConfigurationSidebar = useCallback(() => {
    toggleCommunityConfigurationSidebarEventDispatch({
      action: !isCalledFromOnboarding ? 'close' : 'invalidateQueryOnboarding'
    })

    resetFormGeneralConfigEventDispatch()

    reset()
  }, [isCalledFromOnboarding, reset])

  return (
    <CommunityConfigurationSidebarContext.Provider
      value={{
        isOpen,
        community,
        openCommunityConfigurationSidebar,
        closeCommunityConfigurationSidebar,
        setSelectedStep,
        selectedStep,
        updateCommunity,
        reset
      }}>
      <CommunityConfigurationSidebar
        isOpen={isOpen}
        closeModal={closeCommunityConfigurationSidebar}
        selectedStep={selectedStep}
        setSelectedStep={setSelectedStep}
      />
      {children}
    </CommunityConfigurationSidebarContext.Provider>
  )
}
